import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Clock, CheckCircle2, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { useDataFreshness } from '../../hooks/admin/useDataFreshness';
import type { DataFreshness } from '../../hooks/admin/types/database';
import './admin-premium.css';

export interface FreshnessTimelineProps {
  className?: string;
  /**
   * Maximum number of items to display
   * @default 10
   */
  limit?: number;
  /**
   * Show only specific statuses
   */
  statusFilter?: ('healthy' | 'warning' | 'stale')[];
  /**
   * Auto-refresh interval in seconds
   * @default 30
   */
  refreshInterval?: number;
}

/**
 * FreshnessTimeline component displays data freshness status in a timeline format
 *
 * Features:
 * - Real-time status updates with SWR
 * - Status-based visual indicators
 * - Skeleton loading states
 * - WCAG 2.1 Level AA accessible
 * - Premium design with animations
 *
 * @example
 * ```tsx
 * <FreshnessTimeline limit={5} statusFilter={['warning', 'stale']} />
 * ```
 */
export function FreshnessTimeline({
  className,
  limit = 10,
  statusFilter,
  refreshInterval = 30,
}: FreshnessTimelineProps) {
  const { data, isLoading, isError, mutate } = useDataFreshness();

  // Filter and limit data
  const filteredData = React.useMemo(() => {
    if (!data?.data_types) return [];

    let filtered = data.data_types;

    if (statusFilter && statusFilter.length > 0) {
      filtered = filtered.filter((item) => statusFilter.includes(item.status));
    }

    return filtered.slice(0, limit);
  }, [data, statusFilter, limit]);

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatNextUpdate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);

    if (diffMs < 0) return 'Overdue';
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffHours < 24) return `in ${diffHours}h`;

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: 'healthy' | 'warning' | 'stale') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'stale':
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getJobStatusIcon = (status: 'success' | 'partial' | 'failed') => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'partial':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusBadgeVariant = (status: 'healthy' | 'warning' | 'stale') => {
    switch (status) {
      case 'healthy':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'stale':
        return 'destructive';
    }
  };

  if (isError) {
    return (
      <Card className={cn('p-6', className)}>
        <div className="flex items-center gap-3 text-red-500">
          <XCircle className="h-5 w-5" />
          <div>
            <p className="font-medium">Failed to load data freshness</p>
            <p className="text-sm text-muted-foreground">Please try again later</p>
          </div>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={cn('p-6', className)} role="status" aria-label="Loading data freshness timeline">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <Skeleton className="h-10 w-10 rounded-full" />
                {i < 3 && <div className="w-0.5 h-16 bg-muted mt-2" />}
              </div>
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-full max-w-md" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!data || filteredData.length === 0) {
    return (
      <Card className={cn('p-6', className)}>
        <div className="text-center py-8">
          <Clock className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No data available</p>
          <p className="text-xs text-muted-foreground mt-1">
            {statusFilter ? 'Try adjusting your filters' : 'Data will appear here once available'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn('p-6', className)}
      role="region"
      aria-label="Data freshness timeline"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">Data Freshness</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Last updated: {formatTimestamp(data.timestamp)}
          </p>
        </div>
        <button
          onClick={() => mutate()}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          aria-label="Refresh data freshness"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Timeline */}
      <div className="space-y-6">
        {filteredData.map((item: DataFreshness, index: number) => (
          <div key={item.name} className="flex gap-4">
            {/* Icon column */}
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex items-center justify-center h-10 w-10 rounded-full transition-all',
                  item.status === 'healthy' && 'bg-emerald-500/10',
                  item.status === 'warning' && 'bg-amber-500/10',
                  item.status === 'stale' && 'bg-red-500/10'
                )}
                role="img"
                aria-label={`${item.status} status`}
              >
                {getStatusIcon(item.status)}
              </div>
              {index < filteredData.length - 1 && (
                <div className="w-0.5 h-full min-h-[3rem] bg-border mt-2" />
              )}
            </div>

            {/* Content column */}
            <div className="flex-1 pb-2">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground mb-1">
                    {item.name}
                  </h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant={getStatusBadgeVariant(item.status)}
                      className="text-xs"
                      role="status"
                      aria-label={`Status: ${item.status}`}
                    >
                      {item.status}
                    </Badge>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      {getJobStatusIcon(item.last_job_status)}
                      <span>Last job: {item.last_job_status}</span>
                    </div>
                  </div>
                </div>
                <time
                  className="text-xs text-muted-foreground whitespace-nowrap"
                  dateTime={item.last_updated || undefined}
                >
                  {formatTimestamp(item.last_updated)}
                </time>
              </div>

              {/* Details */}
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Records:</span>
                  <span>
                    {item.records_updated.toLocaleString()} / {item.records_total.toLocaleString()}
                    {item.records_total > 0 && (
                      <span className="ml-1">
                        ({Math.round((item.records_updated / item.records_total) * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  <span>
                    Updates every {item.schedule_interval_minutes}m · Next {formatNextUpdate(item.next_scheduled)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
